// frontend/src/components/crm/LeadForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { leadAPI, usersAPI } from '../../utils/crmApi';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const LeadForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const isEdit = !!id && id !== 'new';
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        source: 'web_form',
        status: 'new',
        assignedTo: '',
        country: '',
        city: '',
        tags: [],
        customFields: {}
    });

    useEffect(() => {
        loadUsers();
        if (isEdit) {
            loadLead();
        }
    }, [id, isEdit]);

    const loadUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            if (response.data?.success) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const loadLead = async () => {
        try {
            setLoading(true);
            const response = await leadAPI.getById(id);
            if (response.data?.success) {
                const lead = response.data.data;
                setFormData({
                    firstName: lead.firstName || '',
                    lastName: lead.lastName || '',
                    email: lead.email || '',
                    phone: lead.phone || '',
                    company: lead.company || '',
                    jobTitle: lead.jobTitle || '',
                    source: lead.source || 'web_form',
                    status: lead.status || 'new',
                    assignedTo: lead.assignedTo?._id || '',
                    country: lead.country || '',
                    city: lead.city || '',
                    tags: lead.tags || [],
                    customFields: lead.customFields || {}
                });
            }
        } catch (error) {
            console.error('Failed to load lead:', error);
            toast.error('Failed to load lead');
            navigate('/leads');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isEdit) {
                await leadAPI.update(id, formData);
                toast.success('Lead updated successfully!');
            } else {
                await leadAPI.create(formData);
                toast.success('Lead created successfully!');
            }
            navigate('/leads');
        } catch (error) {
            console.error('Failed to save lead:', error);
            toast.error(error.response?.data?.message || 'Failed to save lead');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEdit) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center space-x-4">
                <button
                    onClick={() => navigate('/leads')}
                    className="p-2 text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-foreground">
                        {isEdit ? 'Edit Lead' : 'Create Lead'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            First Name
                        </label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Last Name
                        </label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Phone
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Company
                        </label>
                        <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Job Title
                        </label>
                        <input
                            type="text"
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Source
                        </label>
                        <select
                            name="source"
                            value={formData.source}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        >
                            <option value="web_form">Web Form</option>
                            <option value="referral">Referral</option>
                            <option value="social_media">Social Media</option>
                            <option value="email_campaign">Email Campaign</option>
                            <option value="phone_call">Phone Call</option>
                            <option value="website">Website</option>
                            <option value="search">Search</option>
                            <option value="advertisement">Advertisement</option>
                            <option value="event">Event</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Status
                        </label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="qualified">Qualified</option>
                            <option value="nurturing">Nurturing</option>
                            <option value="converted">Converted</option>
                            <option value="disqualified">Disqualified</option>
                            <option value="lost">Lost</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Assigned To
                        </label>
                        <select
                            name="assignedTo"
                            value={formData.assignedTo}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        >
                            <option value="">Unassigned</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>
                                    {user.name || user.email}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={() => navigate('/leads')}
                        className="px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input rounded-lg hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: branding.primaryColor }}
                    >
                        {loading ? 'Saving...' : isEdit ? 'Update Lead' : 'Create Lead'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default LeadForm;


